import { commonComponents as cc } from "@/components/common";
import { schedulesQueryOptions } from "@/features/schedules/api/selectSchedules";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { formatScheduleSummary } from "@patient-watch/forms";
import {
    type ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    type SortingState,
    useReactTable,
} from "@tanstack/react-table";
import { ui } from "@patient-watch/ui";
import type { ScheduleViewT } from "@patient-watch/types";
import dayjs from "dayjs";
import * as React from "react";
import { ArrowUpDown, Eye } from "lucide-react";

const columns: ColumnDef<ScheduleViewT>[] = [
    {
        accessorKey: "schedule_name",
        header: ({ column }) => {
            return (
                <ui.Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    className="hover:bg-transparent p-0"
                >
                    Schedule Name
                    <ArrowUpDown className="ml-2 w-4 h-4" />
                </ui.Button>
            );
        },
        cell: ({ row }) => (
            <Link
                to="/schedules/$scheduleId"
                params={{ scheduleId: row.original.schedule_id || "" }}
                className="font-medium text-blue-600 hover:underline"
            >
                {formatScheduleSummary(row.original)}
            </Link>
        ),
    },
    {
        accessorKey: "schedule_description",
        header: "Description",
        cell: ({ row }) => (
            <div className="max-w-md text-slate-600 text-sm truncate">{row.original.schedule_description || "—"}</div>
        ),
    },
    {
        accessorKey: "timings",
        header: ({ column }) => {
            return (
                <ui.Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    className="hover:bg-transparent p-0"
                >
                    Timings
                    <ArrowUpDown className="ml-2 w-4 h-4" />
                </ui.Button>
            );
        },
        cell: ({ row }) => {
            const timings = row.original.timings || [];
            return <div className="text-center">{timings.length}</div>;
        },
        sortingFn: (rowA, rowB) => {
            const aLength = (rowA.original.timings || []).length;
            const bLength = (rowB.original.timings || []).length;
            return aLength - bLength;
        },
    },
    {
        accessorKey: "is_published",
        header: ({ column }) => {
            return (
                <ui.Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    className="hover:bg-transparent p-0"
                >
                    Status
                    <ArrowUpDown className="ml-2 w-4 h-4" />
                </ui.Button>
            );
        },
        cell: ({ row }) => (
            <div className="text-center">
                {row.original.is_published ? (
                    <span className="bg-green-100 px-2 py-1 rounded-full text-green-800 text-xs">Published</span>
                ) : (
                    <span className="bg-gray-100 px-2 py-1 rounded-full text-gray-800 text-xs">Draft</span>
                )}
            </div>
        ),
    },
    {
        accessorKey: "created_at",
        header: ({ column }) => {
            return (
                <ui.Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    className="hover:bg-transparent p-0"
                >
                    Created
                    <ArrowUpDown className="ml-2 w-4 h-4" />
                </ui.Button>
            );
        },
        cell: ({ row }) => (
            <div className="text-slate-600 text-sm">
                {row.original.created_at ? dayjs(row.original.created_at).format("DD MMM YYYY") : "—"}
            </div>
        ),
    },
    {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
            <Link to="/schedules/$scheduleId" params={{ scheduleId: row.original.schedule_id || "" }}>
                <ui.Button variant="ghost" size="sm">
                    <Eye className="w-4 h-4" />
                </ui.Button>
            </Link>
        ),
    },
];

const Schedules = () => {
    const { schedules } = Route.useLoaderData();
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

    const table = useReactTable({
        data: schedules || [],
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            columnFilters,
        },
    });

    return (
        <section className="my-8 px-4">
            <cc.Title words="Schedules" />

            <div className="border rounded-md">
                <ui.Table className="bg-white/80 rounded-md">
                    <ui.TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <ui.TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <ui.TableHead key={header.id}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </ui.TableHead>
                                ))}
                            </ui.TableRow>
                        ))}
                    </ui.TableHeader>
                    <ui.TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <ui.TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                                    {row.getVisibleCells().map((cell) => (
                                        <ui.TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </ui.TableCell>
                                    ))}
                                </ui.TableRow>
                            ))
                        ) : (
                            <ui.TableRow>
                                <ui.TableCell colSpan={columns.length} className="h-24 text-center">
                                    No schedules found.
                                </ui.TableCell>
                            </ui.TableRow>
                        )}
                    </ui.TableBody>
                </ui.Table>
            </div>

            {/* Pagination */}
            <div className="flex justify-end items-center space-x-2 py-4">
                <div className="flex-1 text-muted-foreground text-sm">
                    {table.getFilteredRowModel().rows.length} schedule(s) total
                </div>
                <div className="space-x-2">
                    <ui.Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        Previous
                    </ui.Button>
                    <ui.Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        Next
                    </ui.Button>
                </div>
            </div>
        </section>
    );
};

export const Route = createFileRoute("/_app/schedules/")({
    component: Schedules,
    loader: async ({ context }) => {
        const schedules = await context?.queryClient.ensureQueryData(schedulesQueryOptions());
        return { schedules };
    },
});
